/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { isNonCurvedLine } from '#utils/PRIVATE_chartUtils/utils';
import { Point } from '#utils/PRIVATE_visSVGUtils';
import { Scale } from '#utils/UNSAFE_visTypes/chart';
import { dvtVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { AllLineType } from '#utils/UNSAFE_visTypes/common/common.types';
import { getCmdsForArea } from '#utils/PRIVATE_visSVGUtils/areaUtils';
import { styles } from '#PRIVATE_Chart/themes/ChartStyles.css';

type AreaSeriesProps = {
  id?: number | string;
  yScale: Scale;
  color: ColorProps['color'];
  lineType?: AllLineType;
  isHorizontal: boolean;
  isLog: boolean;
  areaColorOpacity?: number;
  lineSegments: Point[][];
  bottomCoords?: Point[];
  isHighlighted: boolean;
  isBottomSegmentCurved: boolean;
  seriesIndex: number;
  isRtl: boolean;
  groupWidth?: number;
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency';
  plotAreaClipPathId?: string;
  accessibleLabel?: string;
  activeId?: string;
  variant?: 'normal' | 'refArea';
};

export function AreaSeries({
  yScale,
  lineType = 'straight',
  color,
  isHorizontal,
  isLog,
  areaColorOpacity,
  lineSegments,
  bottomCoords,
  isHighlighted,
  isBottomSegmentCurved,
  seriesIndex,
  groupWidth,
  isRtl,
  timeAxisType,
  plotAreaClipPathId,
  variant = 'normal',
  accessibleLabel,
  activeId,
  id
}: AreaSeriesProps) {
  const style = {
    fill: color,
    opacity: isHighlighted
      ? areaColorOpacity
      : `calc(${dvtVars.dimmedOpacity} * ${areaColorOpacity})`
  };
  return (
    <g clip-path={`url(#${plotAreaClipPathId})`} aria-label={accessibleLabel} id={activeId}>
      {lineSegments.map((lineSegment: Point[]) => {
        if (lineSegment.length < 2) {
          return;
        }

        let baseCoords;
        if (bottomCoords) {
          // custom bottom coords. eg: top of last series in stacked area
          baseCoords = bottomCoords;
        } else {
          // TODO: make this more generic when we support apps setting baseline
          // for log assume domain min is the baseline, otherwise use 0 baseline
          const baselineCoord = yScale.transform(isLog ? yScale.domain()[0] : 0);
          baseCoords = [
            [lineSegment[0][0], baselineCoord],
            [lineSegment[lineSegment.length - 1][0], baselineCoord]
          ] as Point[];
        }
        const cmds = getCmdsForArea(
          lineSegment,
          baseCoords,
          lineType,
          isHorizontal,
          isBottomSegmentCurved,
          isRtl,
          groupWidth,
          timeAxisType
        );
        return variant === 'refArea' ? (
          isNonCurvedLine(lineType) ? (
            <polygon
              points={cmds}
              style={style}
              data-oj-private-ref-obj-id={id}
              data-oj-private-object={'refArea'}
              className={styles.refArea}
            />
          ) : lineType === 'curved' ? (
            <path
              d={cmds}
              style={style}
              data-oj-private-ref-obj-id={id}
              data-oj-private-object={'refArea'}
              className={styles.refArea}
            />
          ) : undefined
        ) : isNonCurvedLine(lineType) ? (
          <polygon
            points={cmds}
            style={style}
            data-oj-private-series-index={seriesIndex}
            data-oj-private-object={'area'}
          />
        ) : lineType === 'curved' ? (
          <path
            d={cmds}
            style={style}
            data-oj-private-series-index={seriesIndex}
            data-oj-private-object={'area'}
          />
        ) : undefined;
      })}
    </g>
  );
}
